import type { LocalDatabase } from './types';
import type { ChangeOperation, ClientChange, SyncedTableName } from '@couplespace/sync';
import { uuidv7 } from 'uuidv7';

export type OutboxEntry = {
  id: string;
  tableName: SyncedTableName;
  operation: ChangeOperation;
  row: Record<string, unknown>;
  baseSyncVersion: number | null;
  createdAt: number;
};

export async function enqueueOutbox(
  database: LocalDatabase,
  entry: Omit<OutboxEntry, 'id' | 'createdAt'>,
): Promise<string> {
  const id = uuidv7();
  await database.runAsync(
    `INSERT INTO sync_outbox (id, table_name, operation, row_json, base_sync_version, created_at)
     VALUES (?, ?, ?, ?, ?, ?)`,
    id,
    entry.tableName,
    entry.operation,
    JSON.stringify(entry.row),
    entry.baseSyncVersion,
    Date.now(),
  );
  return id;
}

export async function listOutbox(database: LocalDatabase): Promise<OutboxEntry[]> {
  const rows = await database.getAllAsync<{
    id: string;
    table_name: SyncedTableName;
    operation: ChangeOperation;
    row_json: string;
    base_sync_version: number | null;
    created_at: number;
  }>(`SELECT * FROM sync_outbox ORDER BY created_at ASC`);

  return rows.map((row) => ({
    id: row.id,
    tableName: row.table_name,
    operation: row.operation,
    row: JSON.parse(row.row_json) as Record<string, unknown>,
    baseSyncVersion: row.base_sync_version,
    createdAt: row.created_at,
  }));
}

export async function removeOutboxIds(database: LocalDatabase, ids: string[]): Promise<void> {
  for (const id of ids) {
    await database.runAsync(`DELETE FROM sync_outbox WHERE id = ?`, id);
  }
}

export function outboxToClientChanges(entries: OutboxEntry[]): ClientChange[] {
  return entries.map((entry) => ({
    table: entry.tableName,
    operation: entry.operation,
    row: entry.row,
    baseSyncVersion: entry.baseSyncVersion,
  }));
}
